import type { Audit, AuditMode, AuditReport } from "@/services/api/guardiansv2/types";

/** True when the audit should use the issue-first report inspector layout. */
export function isIssueFirstAudit(audit: Pick<Audit, "audit_mode" | "origin_alert_id">): boolean {
  if (audit.audit_mode === "issue_first") return true;
  if (audit.audit_mode === "entity") return false;
  return Boolean(audit.origin_alert_id);
}

export function auditModeLabel(mode: AuditMode): string {
  return mode === "issue_first" ? "Issue report" : "Entity audit";
}

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function synthesisSectionsFromReport(report: AuditReport): string[] {
  const sections: string[] = [];
  const push = (value?: string | null) => {
    const text = trimmed(value);
    if (text) sections.push(text);
  };

  push(report.headline);
  push(report.where);
  push(report.what_happened);
  push(report.context);

  const actions = (report.recommended_actions ?? []).map((action) => trimmed(action)).filter(Boolean);
  if (actions.length > 0) {
    sections.push(actions.map((action) => `• ${action}`).join("\n"));
  }

  return sections;
}

/** Full synthesis copy for tabs and legacy fallbacks — not just ``executive_summary``. */
export function resolveSynthesisBody(
  audit: Pick<Audit, "report" | "executive_summary">
): string {
  const report = audit.report;
  if (report) {
    const sections = synthesisSectionsFromReport(report);
    if (sections.length > 0) return sections.join("\n\n");
  }
  return trimmed(audit.executive_summary) ?? "";
}

function nonEmpty(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

/** Drop WS placeholder / empty persisted audits from the list feed. */
export function isAuditListable(audit: Audit): boolean {
  if (!audit.client_id?.trim()) {
    return audit.status === "queued" || audit.status === "running";
  }
  if (audit.status === "queued" || audit.status === "running") {
    return true;
  }
  const hasPersonas =
    audit.personas_completed.length > 0 || Object.keys(audit.personas).length > 0;
  const hasSummary = nonEmpty(audit.inputs?.summary);
  const hasContext = audit.inputs?.guardian_context != null;
  const hasReport =
    nonEmpty(audit.report?.headline) ||
    nonEmpty(audit.executive_summary);
  if (!hasPersonas && !hasSummary && !hasContext && !hasReport) {
    return false;
  }
  return true;
}
